import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { verifyAuthStatus, updateLanguageApi, setStudentSession, clearStudentSession } from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Called on login/register success. Pass the access + refresh tokens from the
   *  auth response to establish a proper session; both are optional so legacy
   *  callers that only have a user object still compile. */
  login: (user: User, token?: string, refreshToken?: string) => void;
  logout: () => void;
  language: 'en' | 'hi';
  setLanguage: (language: 'en' | 'hi') => Promise<void>;
  /** Re-read the user from /api/auth/me, e.g. after the entry assessment
   *  clears `entry_assessment_pending`. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // The axios interceptor dispatches `student-session-expired` when the refresh
  // token itself is rejected (90d idle) or refresh fails. That's the only forced
  // logout — an expired access token is refreshed silently and never reaches us.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener('student-session-expired', onExpired);
    return () => window.removeEventListener('student-session-expired', onExpired);
  }, []);

  useEffect(() => {
    // Check if user is stored in localStorage AND verify it
    const checkAuth = async () => {
      setIsLoading(true);
      const storedUserTokenInfo = localStorage.getItem('user'); // Assume this stores necessary info (like ID for the bearer token)

      if (storedUserTokenInfo) {
        try {
          // Attempt to verify the session with the backend
          // verifyAuthStatus uses the interceptor which reads localStorage itself
          const verifiedUser = await verifyAuthStatus(); 
          
          // If verification is successful, set the user state
          setUser(verifiedUser);
          setIsAuthenticated(true);
          console.log("Session verified successfully.");

        } catch (error) {
          // If verification fails (401 etc.), clear storage and state
          console.error("Session verification failed:", error);
          localStorage.removeItem('user');
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        // No stored info, definitely not logged in
        setUser(null);
        setIsAuthenticated(false);
      }
      
      // Loading finished whether verified, failed, or no token found
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = (user: User, token?: string, refreshToken?: string) => {
    localStorage.setItem('user', JSON.stringify(user));
    // Establish the JWT session when tokens are supplied (new login/register
    // responses). Without them we fall back to the legacy `Bearer <user.id>`
    // path — the backend still accepts it during the transition window.
    if (token && refreshToken) {
      setStudentSession(token, refreshToken);
    }
    setUser(user);
    setIsAuthenticated(true);
  };

  const logout = () => {
    // Clears studentToken + studentRefreshToken + the cached `user`.
    clearStudentSession();
    setUser(null);
    setIsAuthenticated(false);
  };

  // Persist the coach-response language, then sync state + localStorage so the
  // choice survives reloads. Optimistically flips local state; on API failure
  // the caller's error surfaces and state is reverted to the server value.
  const setLanguage = async (language: 'en' | 'hi'): Promise<void> => {
    const updated = await updateLanguageApi(language);
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  };

  const refreshUser = async (): Promise<void> => {
    const fresh = await verifyAuthStatus();
    setUser(fresh);
    localStorage.setItem('user', JSON.stringify(fresh));
  };

  const language: 'en' | 'hi' = user?.language === 'hi' ? 'hi' : 'en';

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, language, setLanguage, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}; 